import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { tieneCoordenadasValidas, filtrarClientesConCoordenadas } from './utils/coordenadas';

// Componentes de la aplicación
import Header from './components/Header';
import Filters from './components/Filters';
import MapContainer from './components/MapContainer';
import SidePanel from './components/SidePanel';
import LoadingSpinner from './components/LoadingSpinner';
import DetailModal from './components/DetailModal';
import StatsModal from './components/StatsModal';
import VisitChangesModal from './components/VisitChangesModal';
import VendorReassignModal from './components/VendorReassignModal';
import Login from './components/Login';
import PrivateRoute from './components/PrivateRoute';
import { clientesService, vendedoresService } from './services/api';

// Contexto de autenticación
import { AuthProvider, useAuth } from './contexts/AuthContext';

const AppContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  position: relative;
`;

const MapSection = styled.div`
  flex: 1;
  position: relative;
`;

const NoResultsAlert = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 15px 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1000;
  max-width: 400px;
  text-align: center;
  
  strong {
    color: #856404;
    font-size: 16px;
    display: block;
    margin-bottom: 8px;
  }
  
  p {
    color: #856404;
    margin: 0 0 8px 0;
    font-size: 14px;
  }
  
  small {
    color: #6c757d;
    font-size: 12px;
  }
`;

// Componente principal de la aplicación
function MainApp() {
  const { user, isAdmin, isSupervisor, isVendedor, codigoVendedor, getCodigosVendedor } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [selectedClientes, setSelectedClientes] = useState([]);
  const [clearSelectionFn, setClearSelectionFn] = useState(null); // Estado para almacenar la función de limpieza
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false); // Estado para controlar la visibilidad del modal de detalle
  const [isPolygonDetailOpen, setIsPolygonDetailOpen] = useState(false); // Modal detalle por polígono
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false); // Estado para controlar la visibilidad del modal de estadísticas
  const [isVisitChangesOpen, setIsVisitChangesOpen] = useState(false); // Modal cambios de visitas
  const [visitChangesDraft, setVisitChangesDraft] = useState({ changesByClient: {}, notas: '' }); // Borrador persistente
  const [isVendorReassignOpen, setIsVendorReassignOpen] = useState(false); // Modal reasignación vendedor
  const [vendorReassignDraft, setVendorReassignDraft] = useState({ changesByClient: {}, notas: '' });
  const [polygonActive, setPolygonActive] = useState(false);
  const [filters, setFilters] = useState({
    vendedor: [],
    dia: '',
    busqueda: '',
    frecuencia: '',
    semanaVisita: ''
  });
  const [isSidePanelCollapsed, setIsSidePanelCollapsed] = useState(false);

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      vendedor: [],
      dia: '',
      busqueda: '',
      frecuencia: '',
      semanaVisita: ''
    });
  };

  const toggleSidePanel = () => {
    setIsSidePanelCollapsed(prev => !prev);
  };

  // Guardar reasignaciones desde modal por filtro (1 vendedor seleccionado)
  const handleGuardarReasignaciones = async (detalles, notas) => {
    try {
      setLoading(true);
      // Validar que haya exactamente un vendedor seleccionado (vendedor origen)
      const vendedorActualId = Array.isArray(filters?.vendedor) && filters.vendedor.length === 1
        ? filters.vendedor[0]
        : null;
      if (!vendedorActualId) {
        const msg = 'Selecciona exactamente un vendedor para realizar la reasignación.';
        toast.error(msg);
        setError(msg);
        return;
      }

      // Agrupar por vendedor destino
      const groups = detalles.reduce((acc, d) => {
        const dest = d.vendedor_nuevo_id;
        if (!dest) return acc;
        if (!acc[dest]) acc[dest] = [];
        acc[dest].push(d.clt_codigo);
        return acc;
      }, {});

      // Ejecutar por grupo (secuencial)
      for (const destId of Object.keys(groups)) {
        const clienteIds = groups[destId];
        if (clienteIds.length === 0) continue;
        await clientesService.reasignarVendedor(clienteIds, destId, notas || '', vendedorActualId);
      }

      // Recargar datos y limpiar estado del modal
      await loadInitialData();
      setVendorReassignDraft({ changesByClient: {}, notas: '' });
      setIsVendorReassignOpen(false);

      // Un único toast final de éxito
      toast.success('Reasignaciones guardadas correctamente');
    } catch (err) {
      const msg = 'Error al guardar reasignaciones: ' + err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Validaciones al abrir modales (bloqueo cruzado)
  const handleOpenVisitChanges = async () => {
    try {
      const resumen = await clientesService.getResumenPendientes();
      if (resumen && Number(resumen.reasignaciones_pendientes) > 0) {
        toast.warning('No puedes abrir Cambios de Visita mientras existan Reasignaciones pendientes. Finaliza o espera la aprobación.');
        await loadInitialData();
        return;
      }
      setIsVisitChangesOpen(true);
    } catch (err) {
      toast.error('Error al validar pendientes: ' + err.message);
    }
  };

  const handleOpenVendorReassign = async () => {
    try {
      const resumen = await clientesService.getResumenPendientes();
      if (resumen && Number(resumen.cambios_visita_pendientes) > 0) {
        toast.warning('No puedes abrir Reasignación de vendedor mientras existan Cambios de Visita pendientes. Finaliza o espera la aprobación.');
        await loadInitialData();
        return;
      }
      setIsVendorReassignOpen(true);
    } catch (err) {
      toast.error('Error al validar pendientes: ' + err.message);
    }
  };

  // Guardar cambios de visitas (día/frecuencia/semana)
  const handleActualizarVisitas = async (detalles, notas) => {
    try {
      setLoading(true);
      // Validar que haya exactamente un vendedor seleccionado
      const vendedorActualId = Array.isArray(filters?.vendedor) && filters.vendedor.length === 1
        ? filters.vendedor[0]
        : null;

      if (!vendedorActualId) {
        const msg = 'Selecciona exactamente un vendedor para aplicar los cambios de visitas.';
        toast.error(msg);
        setError(msg);
        return;
      }

      const resp = await clientesService.actualizarVisitas(detalles, notas, vendedorActualId);
      toast.success(resp?.message || 'Cambios guardados correctamente');
      await loadInitialData();
      setVisitChangesDraft({ changesByClient: {}, notas: '' });
      setIsVisitChangesOpen(false);
      return resp;
    } catch (err) {
      const msg = 'Error al actualizar visitas: ' + err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar datos iniciales
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [clientesData, vendedoresData] = await Promise.all([
        clientesService.getAll(),
        vendedoresService.getAll()
      ]);

      setClientes(clientesData);
      setVendedores(vendedoresData);
    } catch (err) {
      setError('Error al cargar los datos: ' + err.message);
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar clientes según los filtros activos y rol del usuario
  const filteredClientes = clientes.filter(cliente => {
    // Filtro automático por rol del usuario
    const codigosVendedor = getCodigosVendedor();
    
    // Admin puede ver todos los clientes
    if (isAdmin()) {
      // Sin filtro automático adicional
    }
    // Supervisor solo ve clientes de sus rutas asignadas
    else if (isSupervisor() && codigosVendedor.length > 0) {
      if (!codigosVendedor.includes(cliente.clt_ven_codigo)) {
        return false;
      }
    }
    // Vendedor solo ve sus propios clientes
    else if (isVendedor() && codigosVendedor.length > 0) {
      if (!codigosVendedor.includes(cliente.clt_ven_codigo)) {
        return false;
      }
    }
    
    if (filters.vendedor && filters.vendedor.length > 0 && !filters.vendedor.includes(cliente.clt_ven_codigo)) return false;
    if (filters.dia && cliente.clt_diavisita !== filters.dia) return false;
    if (filters.busqueda) {
      const q = filters.busqueda.toLowerCase();
      const nombre = (cliente.clt_nombre || '').toLowerCase();
      const codigo = String(cliente.clt_codigo || '').toLowerCase();
      if (!nombre.includes(q) && !codigo.includes(q)) return false;
    }
    if (filters.frecuencia && cliente.clt_frecuencia !== filters.frecuencia) return false;
    if (filters.semanaVisita && cliente.clt_semanavisita !== filters.semanaVisita) return false;
    return true;
  });

  // Filtrar vendedores según rol del usuario
  const filteredVendedores = isVendedor()
    ? vendedores.filter(v => getCodigosVendedor().includes(v.ven_codigo))
    : vendedores;

  // Verificar si hay filtros activos
  const hasActiveFilters = (filters.vendedor && filters.vendedor.length > 0) || filters.dia || filters.busqueda;
  
  // Mostrar toast si no hay resultados con filtros activos
  useEffect(() => {
    if (hasActiveFilters && filteredClientes.length === 0 && clientes.length > 0) {
      toast.warning('No existen clientes con los filtros seleccionados', {
        position: "top-center",
        autoClose: 5000,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  }, [hasActiveFilters, filteredClientes.length, clientes.length]);

  // Al cambiar el filtro de vendedor: cerrar modal de cambios de visitas y limpiar borrador
  useEffect(() => {
    // Detectar si hay contenido de borrador
    const hasDraft = (
      (visitChangesDraft?.notas || '').trim().length > 0 ||
      Object.keys(visitChangesDraft?.changesByClient || {}).length > 0
    );
    const hasVendorDraft = (
      (vendorReassignDraft?.notas || '').trim().length > 0 ||
      Object.keys(vendorReassignDraft?.changesByClient || {}).length > 0
    );

    if (isVisitChangesOpen || hasDraft) {
      setIsVisitChangesOpen(false);
      setVisitChangesDraft({ changesByClient: {}, notas: '' });
    }
    if (isVendorReassignOpen || hasVendorDraft) {
      setIsVendorReassignOpen(false);
      setVendorReassignDraft({ changesByClient: {}, notas: '' });
    }
  }, [filters.vendedor]);

  // Manejar selección de clientes en el mapa
  const handleClientesSelection = (clientesSeleccionados) => {
    setSelectedClientes(clientesSeleccionados);
  };

  const handleShowPolygonDetails = () => {
    setIsPolygonDetailOpen(true);
  };

  // Reasignar clientes seleccionados
  const handleReasignarClientes = async (nuevoVendedorId, notas) => {
    try {
      setLoading(true);
      
      // Si hay exactamente un vendedor filtrado, usarlo como vendedor origen
      const vendedorActualId = Array.isArray(filters?.vendedor) && filters.vendedor.length === 1
        ? filters.vendedor[0]
        : null;

      const clienteIds = selectedClientes.map(c => c.clt_codigo);
      const resp = await clientesService.reasignarVendedor(clienteIds, nuevoVendedorId, notas, vendedorActualId);
      
      // Recargar datos
      await loadInitialData();
      setSelectedClientes([]);
      
      // Limpiar selección por polígono si la función está disponible
      if (clearSelectionFn) {
        clearSelectionFn();
      }
      
      return resp;
    } catch (err) {
      setError('Error al reasignar clientes: ' + err.message);
      console.error('Error reassigning clients:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && clientes.length === 0) {
    return <LoadingSpinner message="Cargando datos..." />;
  }

  return (
    <AppContainer>
      <Header />
      
      <Filters
        filters={filters}
        setFilters={setFilters}
        vendedores={filteredVendedores}
        onReset={resetFilters}
        onShowDetails={() => setIsDetailModalOpen(true)}
        onShowStats={() => setIsStatsModalOpen(true)}
        onShowVisitChanges={handleOpenVisitChanges}
        onShowVendorReassign={handleOpenVendorReassign}
        polygonActive={polygonActive}
        onShowPolygonDetails={handleShowPolygonDetails}
      />

      <MainContent>
        <SidePanel
          vendedores={filteredVendedores}
          clientes={filteredClientes}
          selectedClientes={selectedClientes}
          onReasignarClientes={handleReasignarClientes}
          loading={loading}
          onReload={loadInitialData}
          clearSelectionAvailable={Boolean(clearSelectionFn)}
          isCollapsed={isSidePanelCollapsed}
          onToggleCollapse={toggleSidePanel}
        />

        <MapSection>
          <MapContainer
            clientes={filteredClientes}
            vendedores={filteredVendedores}
            selectedClientes={selectedClientes}
            onClientesSelection={handleClientesSelection}
            loading={loading}
            error={error}
            onClearSelectionFn={setClearSelectionFn}
            onPolygonActiveChange={setPolygonActive}
          />
        </MapSection>
      </MainContent>

      {/* Modal de Detalle */}
      <DetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        clientes={filteredClientes}
      />

      {/* Modal de Detalle por Polígono */}
      <DetailModal
        isOpen={isPolygonDetailOpen}
        onClose={() => setIsPolygonDetailOpen(false)}
        clientes={selectedClientes}
      />

      {/* Modal de Estadísticas */}
      <StatsModal
        isOpen={isStatsModalOpen}
        onClose={() => setIsStatsModalOpen(false)}
        clientes={clientes}
        vendedores={filteredVendedores}
      />

      {/* Modal de Cambios de Visitas */}
      <VisitChangesModal
        isOpen={isVisitChangesOpen}
        onClose={() => setIsVisitChangesOpen(false)}
        clientes={filteredClientes}
        draft={visitChangesDraft}
        setDraft={setVisitChangesDraft}
        onSave={handleActualizarVisitas}
      />

      {/* Modal de Reasignación por Filtro */}
      <VendorReassignModal
        isOpen={isVendorReassignOpen}
        onClose={() => setIsVendorReassignOpen(false)}
        clientes={filteredClientes}
        vendedores={filteredVendedores}
        draft={vendorReassignDraft}
        setDraft={setVendorReassignDraft}
        onSave={handleGuardarReasignaciones}
      />

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {error && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#f44336',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '4px',
          zIndex: 10000
        }}>
          {error}
          <button 
            onClick={() => setError(null)}
            style={{ marginLeft: '10px', background: 'transparent', border: 'none', color: 'white' }}
          >
            ✕
          </button>
        </div>
      )}
    </AppContainer>
  );
}

// Componente principal que maneja la autenticación y las rutas
function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route 
            path="/" 
            element={
              <PrivateRoute>
                <MainApp />
              </PrivateRoute>
            } 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
